import { Alert, Button, Drawer, Empty, Message, Table, Tag, Typography } from "@arco-design/web-react";
import { Download, FileText, Upload } from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";

import { isHttpClientError } from "../../../lib/http/errors";
import {
  getSafeFulfillmentOrderActionErrorMessage,
  isFulfillmentOrderStaleActionError,
} from "../service/supplierConfirmationService";
import type {
  FulfillmentOrderArchive,
  FulfillmentOrderChangeBatch,
  FulfillmentOrderChangeItem,
  FulfillmentOrderDetail,
} from "../types";
import { FulfillmentOrderAgreementDocument } from "./FulfillmentOrderAgreementDocument";
import { FulfillmentOrderArchiveUploadModal } from "./FulfillmentOrderArchiveUploadModal";
import { formatFulfillmentDateTime } from "../service/fulfillmentOrderListService";
import "../../../shared/ui/crmListPageLayout.css";
import "./fulfillmentOrderAgreementPreview.css";

export type FulfillmentOrderAgreementPreviewProps = {
  detail: FulfillmentOrderDetail | null;
  loading: boolean;
  error?: unknown;
  visible: boolean;
  onClose: () => void;
  onRetry?: () => void;
  /** 抽屉主标题；导游入口可改为「导游确认协议预览」。 */
  title?: string;
  /** 主体入口复用协议正文与文件操作，在同一抽屉补充费用及生成动作。 */
  summary?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  wrapClassName?: string;
  canExport?: boolean;
  canArchive?: boolean;
  exporting?: boolean;
  uploading?: boolean;
  actionPending?: boolean;
  hasUnsavedChanges?: boolean;
  onDownload?: (source: HTMLElement, fileName: string) => Promise<unknown>;
  onDownloadArchive?: (archive: FulfillmentOrderArchive) => Promise<unknown>;
  onUpload?: (file: File) => Promise<unknown>;
};

const formatAmount = (value: string | number | null | undefined) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return value == null ? "0" : String(value);
  return new Intl.NumberFormat("zh-CN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
};

const signedAmount = (value: string | number, action?: FulfillmentOrderChangeItem["action"]) => {
  const amount = Number(value);
  const sign = action === "delete" || amount < 0
    ? "-"
    : action === "add" || amount > 0
      ? "+"
      : "";
  return `${sign}¥${formatAmount(Math.abs(amount))}`;
};

const changeTypeLabel = (batch: FulfillmentOrderChangeBatch) => ({
  amount_adjustment: "金额调整",
  structured_adjustment: "计价调整",
  main_item_added: "新增主项目",
  main_item_changed: "修改主项目",
  main_item_deleted: "删除主项目",
  legacy_change: "历史修改",
}[batch.changeType ?? "legacy_change"]);

const changeItemLabel = (item: FulfillmentOrderChangeItem) => ({
  add: "新增",
  delete: "删除",
  adjust: "金额调整",
}[item.action]);

const changeItemName = (item: FulfillmentOrderChangeItem) => {
  for (const value of [item.snapshot.after, item.snapshot.before, item.snapshot]) {
    if (!value || typeof value !== "object" || Array.isArray(value)) continue;
    const snapshot = value as Record<string, unknown>;
    const name = snapshot.item_name_snapshot ?? snapshot.itemNameSnapshot;
    if (typeof name === "string" && name.trim()) return name.trim();
  }
  return "未命名项目";
};

/** 按供应商维度展示原型一致的确认协议，并仅暴露下载与盖章上传入口。 */
export function FulfillmentOrderAgreementPreviewDrawer({
  detail,
  loading,
  error,
  visible,
  onClose,
  onRetry,
  title = "供应商确认协议预览",
  summary,
  children,
  footer,
  wrapClassName,
  canExport = false,
  canArchive = false,
  exporting = false,
  uploading = false,
  actionPending = false,
  hasUnsavedChanges = false,
  onDownload,
  onDownloadArchive,
  onUpload,
}: FulfillmentOrderAgreementPreviewProps) {
  const [uploadVisible, setUploadVisible] = useState(false);
  const agreementRef = useRef<HTMLElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const restoreTriggerFocus = useCallback(() => {
    if (returnFocusRef.current?.isConnected) returnFocusRef.current.focus();
  }, []);
  const restoreTriggerFocusAfterClose = useCallback(() => {
    window.setTimeout(restoreTriggerFocus, 0);
  }, [restoreTriggerFocus]);
  const focusDrawer = useCallback(() => {
    document
      .querySelector<HTMLElement>(".fulfillment-order-agreement-preview-drawer .arco-drawer-scroll")
      ?.focus();
  }, []);

  useLayoutEffect(() => {
    if (visible) {
      if (document.activeElement instanceof HTMLElement) {
        returnFocusRef.current = document.activeElement;
      }
      return;
    }
    restoreTriggerFocus();
  }, [restoreTriggerFocus, visible]);

  useEffect(() => () => restoreTriggerFocus(), [restoreTriggerFocus]);

  useEffect(() => {
    setUploadVisible(false);
  }, [visible, detail?.order.id, detail?.currentVersion?.id, hasUnsavedChanges]);

  const status = detail?.order.status ?? "";
  const canUploadStampedAgreement = ["draft", "sent", "supplier_confirmed", "effective"]
    .includes(status);
  const compactOrderNo = detail ? `#${detail.order.orderNo.slice(-3)}` : "";
  const amount = formatAmount(detail?.currentVersion?.amount ?? detail?.order.currentAmount);
  const payableBaselineAmount = detail?.order.baselineAmount
    ?? detail?.order.currentAmount
    ?? "0";
  const currentDelta = Number(detail?.order.currentAmount ?? 0) - Number(payableBaselineAmount);
  const errorMessage = isHttpClientError(error)
    && error.code === "FULFILLMENT_ORDER_COMPANY_PARTY_NOT_CONFIGURED"
    ? "协议甲方主体尚未配置，请联系管理员完成系统配置。"
    : "协议详情加载失败，请稍后重试。";

  const fileActions = (
    !loading && !error && detail && ((canExport && onDownload) || (canArchive && onUpload)) ? <>
          <section className="fulfillment-order-protocol-files" role="region" aria-label="协议文件">
            <div>
              <Typography.Title heading={6}>协议文件</Typography.Title>
              <Typography.Text type="secondary">下载确认件给对方确认，双方盖章后上传归档。</Typography.Text>
            </div>
            <div className="fulfillment-order-protocol-file-actions">
              {canExport && detail.currentVersion && onDownload ? (
                <Button
                  icon={<Download size={15} />}
                  loading={exporting}
                  disabled={actionPending || hasUnsavedChanges}
                  onClick={async () => {
                    try {
                      if (hasUnsavedChanges) return;
                      if (!agreementRef.current) throw new Error("协议正文尚未就绪");
                      await onDownload(
                        agreementRef.current,
                        `花儿艺术旅行出行确认件-${detail.order.orderNo}.pdf`,
                      );
                    } catch (error) {
                      if (!isFulfillmentOrderStaleActionError(error)) {
                        Message.error(getSafeFulfillmentOrderActionErrorMessage(
                          error,
                          "下载确认协议失败，请稍后重试",
                        ));
                      }
                    }
                  }}
                >
                  下载确认协议
                </Button>
              ) : null}
              {canArchive && detail.currentVersion && canUploadStampedAgreement && onUpload ? (
                <Button
                  type="primary"
                  icon={<Upload size={15} />}
                  loading={uploading}
                  disabled={actionPending || hasUnsavedChanges}
                  onClick={() => setUploadVisible(true)}
                >
                  上传盖章协议
                </Button>
              ) : null}
            </div>
          </section>
          <FulfillmentOrderArchiveUploadModal
            visible={uploadVisible}
            loading={uploading}
            title="上传盖章协议"
            effectiveOnUpload
            onCancel={() => setUploadVisible(false)}
            onSubmit={async (file) => {
              if (!onUpload || hasUnsavedChanges) return;
              await onUpload(file);
              setUploadVisible(false);
              Message.success("盖章协议已上传，确认件已生效");
            }}
          />
    </> : null
  );

  return (
    <Drawer
      title={(
        <div className="fulfillment-order-protocol-header">
          <div className="fulfillment-order-protocol-heading">
            <span>{title}</span>
            {summary ?? (!loading && !error && detail ? (
              <>
                <h2>{detail.order.settlementSupplierName}</h2>
                <p>
                  {compactOrderNo} · 业务版本 V{detail.order.currentVersionNo ?? 1}
                  {" · "}{detail.lines.length} 条确认项目 · ¥{amount}
                </p>
              </>
            ) : null)}
          </div>
          <Button className="fulfillment-order-protocol-close" onClick={onClose}>关闭</Button>
        </div>
      )}
      closable={false}
      visible={visible}
      autoFocus={false}
      escToExit
      focusLock
      onCancel={onClose}
      afterOpen={focusDrawer}
      afterClose={restoreTriggerFocusAfterClose}
      wrapClassName={`fulfillment-order-agreement-preview-drawer fulfillment-order-contract-workspace ${wrapClassName ?? ""}`}
      headerStyle={{ padding: "10px 14px", borderBottom: "1px solid #e5e6eb" }}
      bodyStyle={{ padding: 10, background: "#f5f7fa" }}
      aria-label={title}
      aria-hidden={!visible}
      width={960}
      footer={fileActions || footer ? <>{fileActions}{footer}</> : null}
      style={{ maxWidth: "100vw" }}
    >
      {loading ? <Typography.Text>正在加载协议详情…</Typography.Text> : error ? (
        <Alert
          type="error"
          showIcon
          content={errorMessage}
          action={onRetry ? <Button size="small" onClick={onRetry}>重试</Button> : undefined}
        />
      ) : detail ? (
        <div className="fulfillment-order-agreement-preview">
          {hasUnsavedChanges ? <Alert type="warning" content="当前预览为已保存内容。请先保存确认行池并处理待加入文本，再下载或归档协议。" /> : null}
          <section className="fulfillment-order-contract-content" role="region" aria-label="合同差异与签后记录">
            <div className="fulfillment-order-contract-overview crm-list-panel" role="group" aria-label="协议金额概览">
              <div className="fulfillment-order-contract-overview__payable"><span>当前应付</span><strong>¥{formatAmount(detail.order.currentAmount)}</strong></div>
              <div><span>当前版本基准</span><strong>¥{formatAmount(payableBaselineAmount)}</strong></div>
              <div><span>当前版本增减</span><strong className="fulfillment-order-contract-money" data-direction={currentDelta > 0 ? "increase" : currentDelta < 0 ? "decrease" : "same"}>{signedAmount(currentDelta)}</strong></div>
            </div>

            <section className="fulfillment-order-contract-document-card crm-list-panel" aria-label="合同正文">
              <header className="fulfillment-order-contract-section-heading crm-list-section-header">
                <h2><FileText size={16} aria-hidden />确认协议</h2>
                <span>业务版本 V{detail.order.currentVersionNo ?? 1}</span>
              </header>
              <div className="fulfillment-order-contract-document-frame">
                <FulfillmentOrderAgreementDocument ref={agreementRef} detail={detail} />
              </div>
            </section>

            <section className="fulfillment-order-contract-records crm-list-panel" aria-label="协议应付增减记录">
              <header className="fulfillment-order-contract-section-heading crm-list-section-header">
                <h2>应付增减项</h2><span>{detail.changes?.length ?? 0} 批记录</span>
              </header>
              <Table<FulfillmentOrderChangeBatch>
                rowKey="id" data={detail.changes ?? []} border={false} pagination={false} size="small"
                className="fulfillment-order-contract-table" scroll={{ x: 860 }}
                noDataElement={<div className="fulfillment-order-contract-empty">暂无应付增减项</div>}
                columns={[
                  { title: "调整类型", key: "type", width: 96, render: (_, batch) => <Tag color="arcoblue">{changeTypeLabel(batch)}</Tag> },
                  { title: "费用项目", key: "items", width: 270, render: (_, batch) => <div className="fulfillment-order-contract-items">
                    {batch.items.length ? batch.items.map(item => <div key={item.id} className="fulfillment-order-contract-item">
                      <span className="fulfillment-order-contract-item-name">{changeItemName(item)}</span>
                      <div className="fulfillment-order-contract-item-meta"><span className="fulfillment-order-contract-item-action">{changeItemLabel(item)}</span><span className="fulfillment-order-contract-item-amount">{signedAmount(item.amount, item.action)}</span></div>
                    </div>) : <span className="fulfillment-order-contract-muted">协议金额调整</span>}
                  </div> },
                  { title: "调整原因", dataIndex: "reason", width: 190, render: (_, batch) => <span className="fulfillment-order-contract-reason">{batch.reason || "—"}</span> },
                  { title: "应付变化", key: "amount", width: 140, align: "right", render: (_, batch) => <div className="fulfillment-order-contract-amount-cell">
                    <strong className="fulfillment-order-contract-money" data-direction={Number(batch.deltaAmount) > 0 ? "increase" : Number(batch.deltaAmount) < 0 ? "decrease" : "same"}>{signedAmount(batch.deltaAmount)}</strong>
                    <span>¥{formatAmount(batch.amountBefore)} → ¥{formatAmount(batch.amountAfter)}</span>
                  </div> },
                  { title: "时间 / 修订", key: "time", width: 164, render: (_, batch) => <div className="fulfillment-order-contract-meta-cell">
                    <time dateTime={batch.createdAt}>{formatFulfillmentDateTime(batch.createdAt)}</time>
                    <span>修订 {batch.expectedRevision} → {batch.revisionAfter}</span>
                  </div> },
                ]}
              />
            </section>

            {children}

            <section className="fulfillment-order-contract-archives crm-list-panel" aria-label="盖章附件历史">
              <header className="fulfillment-order-contract-section-heading crm-list-section-header">
                <h2>盖章附件历史</h2><span>{detail.archives?.length ?? 0} 份附件</span>
              </header>
              {detail.archives?.length ? <Table<FulfillmentOrderArchive>
                rowKey="id" data={detail.archives} border={false} pagination={false} size="small"
                className="fulfillment-order-contract-table" scroll={{ x: 800 }}
                columns={[
                  { title: "文件名称", dataIndex: "originalFileName", width: 270, render: (_, archive) => <div className="fulfillment-order-contract-file-name"><FileText size={18} aria-hidden /><strong>{archive.originalFileName}</strong></div> },
                  { title: "状态", key: "status", width: 100, render: (_, archive) => <Tag color={archive.status === "active" ? "green" : "gray"}>{archive.status === "active" ? "当前有效" : archive.status === "replaced" ? "已替换" : "历史附件"}</Tag> },
                  { title: "附件 / 协议版本", key: "version", width: 150, render: (_, archive) => <div className="fulfillment-order-contract-meta-cell"><strong>附件 V{archive.archiveVersion ?? 1}</strong><span>协议修订 {archive.documentRevision ?? archive.versionId}</span></div> },
                  { title: "归档时间", key: "time", width: 190, render: (_, archive) => <time dateTime={archive.archivedAt}>{formatFulfillmentDateTime(archive.archivedAt)}</time> },
                  { title: "操作", key: "action", width: 90, align: "right", render: (_, archive) => onDownloadArchive && archive.downloadUrl ? <Button
                    type="text" size="small" icon={<Download size={14} />} disabled={actionPending}
                    aria-label={`下载附件 ${archive.originalFileName}`}
                    onClick={async () => {
                      try { await onDownloadArchive(archive); }
                      catch (error) {
                        if (!isFulfillmentOrderStaleActionError(error)) Message.error(getSafeFulfillmentOrderActionErrorMessage(error, "下载盖章附件失败，请稍后重试"));
                      }
                    }}
                  >下载</Button> : <span className="fulfillment-order-contract-muted">—</span> },
                ]}
              /> : <div className="fulfillment-order-contract-empty fulfillment-order-contract-empty--files"><FileText size={24} aria-hidden /><div><strong>暂无盖章附件</strong><span>上传后的盖章文件及历史版本将展示在这里</span></div></div>}
            </section>
          </section>

        </div>
      ) : children ? null : <Empty description="暂无协议详情" />}
      {(loading || error || !detail) ? children : null}
    </Drawer>
  );
}
